// @ts-check
import React, { memo, useMemo, useCallback, useState } from 'react';
import styles from './Panel.module.scss';
import panels from './panels';
import dataSources from './datasources';
import { Button, ButtonVariant, Popover, Split, SplitItem, Tooltip } from '@patternfly/react-core';
import ErrorBoundary from '../../utils/ErrorBoundary';

import _ from 'lodash';
import { useTranslation } from '../../localization/useTranslation';
import useReloadTrigger from '../../utils/hooks/useReloadTrigger';
import { useResizeDetector } from 'react-resize-detector';
import { Icons } from '../Common';
import useBoolean from '../../utils/hooks/useBoolean';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import Markdown from '../Help/Markdown';
import { panelObjects } from './panels';

/**
 * Info to show when a panel type is not found
 * @returns {JSX.Element}
 */
const MissingPanelType = () => {
  const T = useTranslation();
  return <>{ T.dashboard.panel.unknownType }</>;
};

/**
 * Fallback component to show when a panel throws an error
 * @returns {JSX.Element}
 */
const PanelFallback = () => {
  const T = useTranslation();
  return T.common.error.generic;
};

/**
 * @typedef {object} DataSourceComponentProps
 * @property {Array<React.FunctionComponent<any>>} subsources - Data source components
 * @property {(data: Record<string, any>) => JSX.Element} children
 */

/**
 * Creates a recursive data source component to handle hooks in each component
 * @param {DataSourceComponentProps} props
 * @returns {JSX.Element}
 */
const DataSourceComponent = ({ subsources, children }) => {
  const Component = _.head(subsources);
  if (subsources.length > 1) {
    return (<Component>{ data => {
      return (<DataSourceComponent subsources={_.tail(subsources)}>{
        subData => children(_.merge(subData, data))
      }</DataSourceComponent>);
    }
    }</Component>);
  }

  return <Component>{ data => children(data) }</Component>;
};

/**
 * Get panel properties for a panel type
 * @param {string} panelType
 * @param {Record<string, object>} panelProperties
 * @returns {Record<string, object>}
 */
const panelProperties = (panelType, panelProperties) => {
  const panelComponent = panelObjects[panelType];
  return _.isFunction(panelComponent?.properties) ? panelComponent?.properties(panelProperties) : panelComponent?.properties;
};

/**
 * @typedef {object} PanelDataProps
 * @property {Panel} panel
 * @property {Record<string, any>} properties
 * @property {number} reloadTrigger
 * @property {() => Date} dashboardDate
 * @property {(data?: Record<string, any>) => JSX.Element} children
 */


/**
 * Creates a tree of data source components and sends the merge data to the last child component
 * as a render property function.
 * Ex: <PanelData properties..>{ data => ... this is all the data source data, use it in panel... }</PanelData>
 * @param {PanelDataProps} props
 * @returns {JSX.Element}
 */
const PanelData = ({ panel, properties, reloadTrigger, dashboardDate, children: mainChildren }) => {
  // Generate data source hooks components and other constant properties
  // This will then be rendered recursively in order to replace data source on the fly, which does not work
  // if they were pure hooks
  const dataComponents = useMemo(() => {
    const defaultProperties = panelProperties(panel.type, properties);

    return _.map(properties, (property, propertyName) => {
      const defaultProperty = defaultProperties?.[propertyName];
      const derivedPropertyType = defaultProperty?.type;
      // In case of where we don't have a type on the property
      // we will try to use the type of the default property.
      // This can happen if the panel has a new data source that was
      // not saved with the type set on the property
      const type = property?.type ?? derivedPropertyType;
      if (_.isObject(property) && type) {
        return ({ children }) => {
          const sourceData = dataSources[type]?.source({ ..._.omit(property, 'type'), reloadTrigger, dashboardDate });
          return <>{ children({ [propertyName]: sourceData }) }</>;
        };
      }

      // Constant property value
      return ({ children }) => {
        return <>{ children({ [propertyName]: property }) }</>;
      };
    });
  }, [dashboardDate, panel.type, properties, reloadTrigger]);

  if (_.isEmpty(dataComponents)) {
    return mainChildren();
  }
  return <DataSourceComponent subsources={dataComponents} >{ data => mainChildren(data) }</DataSourceComponent>;
};

/**
 * @typedef {object} PanelProps
 * @property {Panel} panel
 * @property {boolean} isVisible - If the panel should be hidden (overriden by onEdit, which will always show the panel if not null)
 * @property {Function} onEditPanel - Callback to edit the panel. If null, the panel is not editable
 * @property {() => Date} dashboardDate - Global dashboard date
 * @property {React.MouseEventHandler<HTMLDivElement>} onClickTitle
 */

/**
 * @param {PanelProps} props
 * @returns {JSX.Element}
 */
const Panel = ({ panel, onEditPanel, isVisible, dashboardDate, onClickTitle }) => {
  const size = useResizeDetector();
  const T = useTranslation();
  const [title, setTitle] = useState(panel.title);

  const onClickEdit = useCallback(() => {
    onEditPanel?.(panel);
  }, [panel, onEditPanel]);

  const [reloadTrigger, triggerReload] = useReloadTrigger();
  const [hasFullScreen, , , toggleFullscreen] = useBoolean(false);
  // An API that panel content can use to update various common properties of the panel
  const api = useMemo(() => {
    return { title, setTitle, size, reload: triggerReload };
  }, [title, size, triggerReload]);

  const PanelComponent = panels[panel.type];
  const inEditMode = !!onEditPanel;
  if (!isVisible && !inEditMode) {
    return null;
  }

  const panelComponent = (
    <div className={classNames(styles.panelContainer, hasFullScreen && styles.panelFullscreen)} ref={size.ref}>
      <Split className={styles.panelTitle} onMouseDown={onClickTitle} hasGutter>
        <SplitItem className={styles.titleContent} isFilled>
          { panel.title }
        </SplitItem>
        { (!_.isEmpty(panel.help)) &&
          <SplitItem>
            <Popover
              aria-label="dashboard help text"
              bodyContent={<Markdown markdown={panel.help} />}
            >
              <div className={styles.verticalCenter}><Icons.Help /></div>
            </Popover></SplitItem>
        }
        <Split>
          <SplitItem>
            <Tooltip content={T.common.togglefullscreen} >
              <Button variant={ButtonVariant.plain} onClick={toggleFullscreen} icon={hasFullScreen ? <Icons.ExitFullscreen /> : <Icons.Fullscreen />} />
            </Tooltip>
          </SplitItem>
          { !hasFullScreen && onEditPanel && <SplitItem>
            <Tooltip content={T.common.edit} >
              <Button variant={ButtonVariant.plain} onClick={onClickEdit} icon={<Icons.Edit />} />
            </Tooltip>
          </SplitItem>
          }
        </Split>
      </Split>
      <div className={classNames(styles.panelContent, inEditMode && !isVisible && styles.panelVisibilityHiddenBorder)}>
        <ErrorBoundary fallback={<PanelFallback />}>
          <PanelData panel={panel} properties={panel.properties} reloadTrigger={reloadTrigger} dashboardDate={dashboardDate}>{
            (data) => {
              return (PanelComponent && <PanelComponent api={api} panel={panel} data={data} />)
                || <MissingPanelType />;
            }
          }</PanelData>
        </ErrorBoundary>
      </div>
    </div>
  );

  return hasFullScreen ? ReactDOM.createPortal(panelComponent, document.body) : panelComponent;
};

export default memo(Panel);
